import { useDispatch, useSelector } from "react-redux";
import { Grid, Stack, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import PersonIcon from "@mui/icons-material/PersonTwoTone";
import { getIsCommercialOrBusinessApplication, getIsConsumerOrPersonalApplication } from "@/constants";
import {
  applicationSelector,
  userDetailsSelector,
  createCustomer,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/applicationFormSlice";
import type {
  ApplicationFormSlice,
  ApplicationState,
  UserDetailsState,
} from "@/store/slices/types/applicationFormSlice";
// @ts-expect-error: ignoring as it is existing js file
import { userPermissionsSelector } from "@/store/slices/userSlice";
import type { UserSlice, UserPermissionsDocument } from "@/store/slices/types/userSlice";
import LoanSummaryDemo from "./LoanSummaryDemo";
// @ts-expect-error: ignoring as it is existing js file
import LoanDetails from "../LoanDetails/index";
// @ts-expect-error: ignoring as it is existing js file
import convertToOrdinal from "../../utils/convertToOrdinal";
// @ts-expect-error: ignoring as it is existing js file
import CustomerDetails from "../CustomerDetails";
// @ts-expect-error: ignoring as it is existing js file
import AddedEntity from "../Entity/AddedEntity";
// @ts-expect-error: ignoring as it is existing js file
import IntroducerDeclaration from "../IntroducerDeclaration";
// @ts-expect-error: ignoring as it is existing js file
import NCCPDeclaration from "../NCCPDeclaration";
// @ts-expect-error: ignoring as it is existing js file
import Entity from "../Entity/index";
// @ts-expect-error: ignoring as it is existing js file
import { getCustomerRole } from "../Utils/helperFunction";

interface DemoApplicationFormProps {
  guarantorFieldErrors: Record<string, string>;
  setGuarantorFieldErrors: (errors: Record<string, string>) => void;
  guarantorsError: Record<string, string>[];
  setGuarantorsError: (errors: Record<string, string>[]) => void;
  entityFieldErrors: Record<string, string>;
  setEntityFieldErrors: (errors: Record<string, string>) => void;
  agreementsFieldErrors: Record<string, string>;
  setAgreementsFieldErrors: (errors: Record<string, string>) => void;
  nccpFieldErrors: Record<string, string>;
  setNccpFieldErrors: (errors: Record<string, string>) => void;
}

const DemoApplicationForm = ({
  guarantorFieldErrors,
  setGuarantorFieldErrors,
  guarantorsError,
  setGuarantorsError,
  entityFieldErrors,
  setEntityFieldErrors,
  agreementsFieldErrors,
  setAgreementsFieldErrors,
  nccpFieldErrors,
  setNccpFieldErrors,
}: DemoApplicationFormProps) => {
  const dispatch = useDispatch();
  const application = useSelector<ApplicationFormSlice, ApplicationState>(applicationSelector);
  const customerRole = getCustomerRole(application.applicationType, application.entities, application.purpose);
  const isCommercialOrBusinessApplication = getIsCommercialOrBusinessApplication(application.applicationType);
  const isConsumerOrPersonalApplication = getIsConsumerOrPersonalApplication(application.applicationType);
  const { validationRequireFieldError } = useSelector<ApplicationFormSlice, UserDetailsState>(userDetailsSelector);
  const permissions = useSelector<UserSlice, UserPermissionsDocument>(userPermissionsSelector);
  const noEditPermissions = !permissions?.application?.edit || !application?.canEditApplication;

  const addCustomer = () => {
    dispatch(
      createCustomer({
        applicationId: application._id,
        entities: application?.entities,
      }),
    );
  };

  return (
    <>
      {/* Loan summary */}
      <LoanSummaryDemo />

      {/* LoanDetails */}
      <LoanDetails applicationType={application.applicationType} isDisableEdit={noEditPermissions} />

      {/* Entity */}
      {isCommercialOrBusinessApplication ? (
        <>
          {application?.entities?.map((entity, i) => (
            <Entity
              key={i}
              application={application}
              entitys={entity}
              entities={application?.entities}
              index={i}
              fieldsErrors={entityFieldErrors}
              setFieldsErrors={setEntityFieldErrors}
              isDisableEdit={noEditPermissions}
            />
          ))}
          <AddedEntity fieldsErrors={entityFieldErrors} setFieldsErrors={setEntityFieldErrors} isDisableEdit={noEditPermissions} />
        </>
      ) : null}

      {/* Customer Details */}
      {application?.customers?.length > 0 &&
        application?.customers?.map((customer, i) => (
          <CustomerDetails
            key={application._id}
            customer={customer}
            fieldErrors={guarantorsError}
            setFieldErrors={setGuarantorsError}
            applicationType={application.applicationType}
            applicationId={application._id}
            index={i}
          />
        ))}

      {application?.customers?.length < 1 && (
        <Grid
          container
          item
          xs={12}
          id={`add${customerRole}`}
          style={{
            margin: "0 0 30px",
            padding: "0 0 20px",
          }}
        >
          <Grid item xs={12} md={2}>
            <Stack direction="row">
              <PersonIcon style={{ marginRight: "10px" }} />
              <Typography>Add {customerRole}</Typography>
            </Stack>
          </Grid>
          <Grid
            item
            xs={12}
            md={10}
            style={{
              padding: "0 0 10px 10px",
              borderBottom: "1px solid rgba(0,0,0,0.12)",
            }}
          >
            <Typography fontWeight={600} style={{ marginBottom: "15px" }}>
              Add {customerRole}
            </Typography>
            <LoadingButton
              variant="outlined"
              disabled={noEditPermissions}
              onClick={() => {
                addCustomer();
                const guarantorFieldErrors = {
                  yearsAt: "",
                  monthsAt: "",
                  IDExpiryDate: "",
                };
                setGuarantorFieldErrors({
                  customerTitle: "",
                  firstName: "",
                  lastName: "",
                  IDNumber: "",
                  dateOfBirth: "",
                  IDCardNumber: "",
                  phoneNumber: "",
                  emailAddress: "",
                  address: "",
                  ...(isCommercialOrBusinessApplication ? guarantorFieldErrors : undefined),
                });
              }}
              color={!!validationRequireFieldError?.customerErrors?.customerRequired ? "error" : "primary"}
            >
              Add {customerRole}
            </LoadingButton>
            {!!validationRequireFieldError?.customerErrors?.customerRequired && (
              <Typography
                style={{
                  marginTop: "5px",
                  color: "#d32f2f",
                  fontSize: "0.75rem",
                  fontWeight: "400",
                }}
              >
                {validationRequireFieldError.customerErrors.customerRequired}
              </Typography>
            )}
          </Grid>
        </Grid>
      )}

      {application?.customers?.length > 0 && (
        <Grid
          container
          item
          xs={12}
          id={`add${customerRole}`}
          style={{
            margin: "0 0 30px",
            padding: "0 0 20px",
            borderBottom: "1px solid rgba(0,0,0,0.12)",
          }}
        >
          <Grid item xs={12} md={2}>
            <Stack direction="row">
              <PersonIcon style={{ marginRight: "10px" }} />
              <Typography>Add {customerRole}</Typography>
            </Stack>
          </Grid>
          <Grid
            item
            xs={12}
            md={10}
            style={{
              padding: "0 0 0 10px",
            }}
          >
            <LoadingButton color="secondary" variant="contained" disabled={noEditPermissions} onClick={addCustomer}>
              Add {convertToOrdinal(application?.customers?.length)} {customerRole}
            </LoadingButton>
          </Grid>
        </Grid>
      )}

      {isConsumerOrPersonalApplication && (
        <>
          <IntroducerDeclaration
            introducerDeclarationAgree={application?.introducerDeclarationAgree}
            applicationId={application._id}
            errorField={agreementsFieldErrors}
            setErrorField={setAgreementsFieldErrors}
            isDisableEdit={noEditPermissions}
          />
          <NCCPDeclaration
            whatIsThePurposeOfThisApplication={
              application?.customers?.[0]?.customerDisclaimer?.whatIsThePurposeOfThisApplication
            }
            whyDoesThisLoanTermSuitYourRequirements={
              application?.customers?.[0]?.customerDisclaimer?.whyDoesThisLoanTermSuitYourRequirements
            }
            whatAreYouLookingForInYourLoan={
              application?.customers?.[0]?.customerDisclaimer?.whatAreYouLookingForInYourLoan
            }
            anyForeseeableChangesImpactingLoanRepayment={
              application?.customers?.[0]?.customerDisclaimer?.anyForeseeableChangesImpactingLoanRepayment
            }
            howApplicantIntendToDoLoanRepaymentAfterChange={
              application?.customers?.[0]?.customerDisclaimer?.howApplicantIntendToDoLoanRepaymentAfterChange
            }
            whyIsThisLoanNotUnsuitableForTheApplicantDetail={
              application?.customers?.[0]?.customerDisclaimer?.whyIsThisLoanNotUnsuitableForTheApplicantDetail
            }
            increaseInLoanAmountForFinancingCostOfInsurance={
              application?.customers?.[0]?.customerDisclaimer?.increaseInLoanAmountForFinancingCostOfInsurance
            }
            balloonPaymentAtTheEndOfTheLoan={
              application?.customers?.[0]?.customerDisclaimer?.balloonPaymentAtTheEndOfTheLoan
            }
            whatToDoWithBalloonPaymentAtTheEndOfTheLoanTerm={
              application?.customers?.[0]?.customerDisclaimer?.whatToDoWithBalloonPaymentAtTheEndOfTheLoanTerm
            }
            application={application}
            customerId={application?.customers?.[0]?._id}
            errorField={nccpFieldErrors}
            setErrorField={setNccpFieldErrors}
            isDisableEdit={noEditPermissions}
          />
        </>
      )}
    </>
  );
};

export { DemoApplicationForm };
